import { ButtonProps, Button } from '@cad-arc';
import React, { useEffect, useState } from 'react';
import styles from './unread-button.module.css';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/use-additional-infos';
import { observer } from 'mobx-react-lite';
import { useVirtualizerContext } from '../../additional-info-renderer/use-row-virtualizer';
import classnames from 'classnames';
import { sleep } from '@m43/core';

const UnreadArrowButton = ({
    position,
    urgent = false,
    ...rest
}: ButtonProps & {
    children?: React.ReactNode;
    onClick?: (e?: unknown) => void;
    urgent?: boolean;
    position: 'top' | 'bottom';
}): React.JSX.Element => {
    return (
        <Button
            variant="solid"
            trailingVisual={position === 'top' ? 'ArrowUp' : 'ArrowDown'}
            className={classnames(
                position === 'top' ? styles['top-button'] : styles['bottom-button'],
                urgent ? styles['urgent-button'] : styles['not-urgent-button']
            )}
            {...rest}
        />
    );
};

export const UnreadButton = observer(() => {
    const { getVirtualItems, scrollToIndex } = useVirtualizerContext();
    const {
        fetchNextPage,
        getIndexById,
        hasNextPage,
        isFetchingNextPage,
        list,
        pageFetchFailed,
        unreadInfos,
    } = useAdditionalInfos();
    const [scrollTargetInfoId, setScrollTargetInfoId] = useState<number | null>(null);

    const scrollToInfoId = async (id: number): Promise<void> => {
        const index = getIndexById(id);
        if (Number.isInteger(index)) {
            // scrollToIndex isn't aware of long infos, so it estimates how far to scroll.
            // Note we don't mark it as read here. If after two scrolls we've still missed it,
            // we don't want to mark it as read. Leave it up to `useUpdateVisibleRead` to handle
            // markAsRead functionality, since it's tied _directly_ to what's visible.
            scrollToIndex(index as number, { align: 'center' });
            await sleep(10);
            scrollToIndex(index as number, { align: 'center' });
        }
        setScrollTargetInfoId(null);
    };

    // this useEffect will load pages until the scroll target info id has been loaded, and then
    // scroll to that row
    useEffect(() => {
        if (scrollTargetInfoId === null || list.length === 0) {
            return;
        }

        const lastLoadedInfoId = list[list.length - 1].id;
        if (scrollTargetInfoId >= lastLoadedInfoId) {
            scrollToInfoId(scrollTargetInfoId);
        } else if (hasNextPage && !isFetchingNextPage && !pageFetchFailed) {
            fetchNextPage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list.length, hasNextPage, isFetchingNextPage, scrollTargetInfoId, pageFetchFailed]);

    const virtualItems = getVirtualItems();

    const lastRenderedItem = virtualItems[virtualItems.length - 1]
        ? list[virtualItems[virtualItems.length - 1].index]
        : undefined;
    const firstRenderedItem = list[virtualItems[0]?.index ?? 0];

    if (!lastRenderedItem || !firstRenderedItem) return null;

    const showTopUrgentButton =
        unreadInfos.HIGH_PRIORITY_UPDATE &&
        firstRenderedItem.id < unreadInfos.HIGH_PRIORITY_UPDATE.id;
    const showTopNewButton =
        !showTopUrgentButton &&
        unreadInfos.STANDARD_UPDATE &&
        list[virtualItems[0].index].id < unreadInfos.STANDARD_UPDATE.id;
    const showBottomUrgentButton =
        unreadInfos.HIGH_PRIORITY_UPDATE &&
        lastRenderedItem.id > unreadInfos.HIGH_PRIORITY_UPDATE.id;
    const showBottomNewButton =
        !showBottomUrgentButton &&
        unreadInfos.STANDARD_UPDATE &&
        lastRenderedItem.id > unreadInfos.STANDARD_UPDATE.id;

    const scrollToUrgentInfo = (): void => {
        unreadInfos?.HIGH_PRIORITY_UPDATE?.id !== undefined &&
            setScrollTargetInfoId(unreadInfos.HIGH_PRIORITY_UPDATE.id);
    };

    const scrollToNewInfo = (): void => {
        unreadInfos?.STANDARD_UPDATE?.id !== undefined &&
            setScrollTargetInfoId(unreadInfos.STANDARD_UPDATE.id);
    };

    return (
        <div className={styles['parent']}>
            {showTopUrgentButton && (
                <UnreadArrowButton
                    position="top"
                    urgent
                    onClick={scrollToUrgentInfo}
                    data-testid="URGENT_UPDATE_ABOVE_BUTTON"
                >
                    Urgent Update
                </UnreadArrowButton>
            )}
            {showTopNewButton && (
                <UnreadArrowButton
                    position="top"
                    onClick={scrollToNewInfo}
                    data-testid="NEW_MESSAGE_ABOVE_BUTTON"
                >
                    New Message
                </UnreadArrowButton>
            )}
            {showBottomUrgentButton && (
                <UnreadArrowButton
                    position="bottom"
                    urgent
                    onClick={scrollToUrgentInfo}
                    data-testid="URGENT_UPDATE_BELOW_BUTTON"
                >
                    Urgent Update
                </UnreadArrowButton>
            )}
            {showBottomNewButton && (
                <UnreadArrowButton
                    position="bottom"
                    onClick={scrollToNewInfo}
                    data-testid="NEW_MESSAGE_BELOW_BUTTON"
                >
                    New Message
                </UnreadArrowButton>
            )}
        </div>
    );
});
